import { FC, memo } from 'react';
import { Tooltip } from 'antd';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import styles from './style.module.css';
import { SideMenuItem } from './SideMenuItem';
import type { SideMenuGroup as SideMenuGroupType } from './types';

interface Props {
  group: SideMenuGroupType;
  expanded: boolean;
  activeKey: string | null;
}

const SideMenuGroupComponent: FC<Props> = ({ group, expanded, activeKey }) => {
  const isDirectActive = group.key === activeKey;
  const hasActiveChild = group.items.some((item) => item.key === activeKey);
  const isActive = isDirectActive || hasActiveChild;

  const headerContent = (
    <>
      <span className={styles.groupIcon} aria-hidden="true">
        {group.icon}
      </span>
      <span className={styles.groupLabel}>{group.label}</span>
    </>
  );

  const headerClassName = cn(styles.groupHeader, {
    [styles.groupHeaderActive]: isActive,
    [styles.groupHeaderLink]: group.path,
  });

  const header = group.path ? (
    <Link
      to={group.path}
      className={headerClassName}
      aria-current={isActive ? 'page' : undefined}
      data-testid={`side-menu-group-${group.key}`}
    >
      {headerContent}
    </Link>
  ) : (
    <div
      className={cn(styles.groupHeader, {
        [styles.groupHeaderActive]: isActive,
      })}
    >
      {headerContent}
    </div>
  );

  return (
    <div className={styles.group} role="group" aria-label={group.label}>
      {expanded ? (
        header
      ) : (
        <Tooltip title={group.label} placement="right" mouseEnterDelay={0.2}>
          {header}
        </Tooltip>
      )}

      {group.items.length > 0 && (
        <div className={styles.itemListWrapper} aria-hidden={!expanded}>
          <ul className={styles.itemList}>
            {group.items.map((item, idx) => (
              <li
                key={item.key}
                className={styles.itemListRow}
                style={{
                  transitionDelay: expanded ? `${80 + idx * 35}ms` : undefined,
                }}
              >
                <SideMenuItem
                  item={item}
                  active={item.key === activeKey}
                  expanded={expanded}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const SideMenuGroup = memo(SideMenuGroupComponent);
SideMenuGroup.displayName = 'SideMenuGroup';
